import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { getTutorialContent } from "./tutorialContent";
import { tutorialPageIdFromPath } from "./tutorialRoutes";

const TutorialContext = createContext({
  pageId: null,
  content: null,
  hasTutorial: false,
  open: false,
  openTutorial: () => {},
  dismiss: () => {},
  showNextLogin: true,
  setShowNextLogin: () => {},
  tutorialsEnabled: false,
  setTutorialsEnabled: () => {},
});

export function useTutorial() {
  return useContext(TutorialContext);
}

function normalizePrefs(raw) {
  if (!raw || typeof raw !== "object") {
    return { enabled: false, never: [] };
  }
  return {
    enabled: raw.enabled === true,
    never: Array.isArray(raw.never) ? raw.never.filter(Boolean).map(String) : [],
  };
}

function sessionKey(userId, pageId) {
  return `ss_tutorial_dismissed_${userId}_${pageId}`;
}

export function clearTutorialSessionDismissals() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("ss_tutorial_dismissed_")) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function TutorialProvider({ user, isAuthenticated, onTutorialPrefsChange, children }) {
  const location = useLocation();
  const pageId = tutorialPageIdFromPath(location.pathname);
  const content = pageId ? getTutorialContent(pageId) : null;
  const prefs = useMemo(() => normalizePrefs(user?.tutorial_prefs), [user?.tutorial_prefs]);

  const [open, setOpen] = useState(false);
  const [showNextLogin, setShowNextLogin] = useState(true);
  const skipAutoOpenRef = useRef(new Set());
  const neverKey = prefs.never.join("|");

  const persistPrefs = useCallback((next) => {
    onTutorialPrefsChange?.(next);
    api.patch("/auth/preferences", { tutorial_prefs: next }).catch(() => {});
  }, [onTutorialPrefsChange]);

  const wasDismissedThisSession = useCallback((userId, id) => {
    const skipKey = `${userId}:${id}`;
    if (skipAutoOpenRef.current.has(skipKey)) return true;
    try {
      return !!sessionStorage.getItem(sessionKey(userId, id));
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !pageId || !content) {
      setOpen(false);
      return;
    }
    const dismissed = wasDismissedThisSession(user.id, pageId);
    if (!prefs.enabled || prefs.never.includes(pageId) || dismissed) {
      setOpen(false);
      return;
    }
    setShowNextLogin(true);
    setOpen(true);
  }, [isAuthenticated, user?.id, pageId, content, prefs.enabled, neverKey, prefs.never, wasDismissedThisSession]);

  const openTutorial = useCallback(() => {
    if (!content) return;
    setShowNextLogin(!prefs.never.includes(pageId));
    setOpen(true);
  }, [content, pageId, prefs.never]);

  const dismiss = useCallback(() => {
    if (user?.id && pageId) {
      skipAutoOpenRef.current.add(`${user.id}:${pageId}`);
      try {
        sessionStorage.setItem(sessionKey(user.id, pageId), "1");
      } catch {
        // ignore
      }
      if (!showNextLogin) {
        if (!prefs.never.includes(pageId)) {
          persistPrefs({ enabled: prefs.enabled, never: [...prefs.never, pageId] });
        }
      } else if (prefs.never.includes(pageId)) {
        persistPrefs({
          enabled: prefs.enabled,
          never: prefs.never.filter((id) => id !== pageId),
        });
      }
    }
    setOpen(false);
  }, [user?.id, pageId, showNextLogin, prefs.enabled, prefs.never, persistPrefs]);

  const setTutorialsEnabled = useCallback((enabled) => {
    persistPrefs({ enabled: !!enabled, never: prefs.never });
  }, [persistPrefs, prefs.never]);

  const value = useMemo(() => ({
    pageId,
    content,
    hasTutorial: !!content && !!isAuthenticated,
    open,
    openTutorial,
    dismiss,
    showNextLogin,
    setShowNextLogin,
    tutorialsEnabled: prefs.enabled,
    setTutorialsEnabled,
  }), [
    pageId,
    content,
    isAuthenticated,
    open,
    openTutorial,
    dismiss,
    showNextLogin,
    prefs.enabled,
    setTutorialsEnabled,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}
